import { BarFilter, FilterBar, ICurrentFilter, IData, IFilterBarRef } from '@starryui10/filters/filterBar';
import { HStack } from '@starryui10/layouts/Stack';
import React, { createRef, useEffect } from 'react';
import { FixedFilters } from '../../components/FixedFilters';
import { SaveView } from '../../components/SaveView';
import { SaveViewProvider } from '../../components/SaveView/context/SaveViewContext';
import { TooComplexBar } from '../../components/TooComplexBar';
import { useAppContext } from '../../context/AppContext';
import { useLazyAllApplicationStatuses } from '../../graphql/useLazyAllApplicationStatusQuery';
import { useLazyAllCategories } from '../../graphql/useLazyAllCategoriesQuery';
import { useLazyAllCommunity } from '../../graphql/useLazyAllCommunityQuery';
import { useLazyAllEntryStatus } from '../../graphql/useLazyAllEntryStatusQuery';
import { useLazyAllGenders } from '../../graphql/useLazyAllGendersQuery';
import { useLazyAllRoomTypes } from '../../graphql/useLazyAllRoomTypesQuery';
import { useLazyAllTerm } from '../../graphql/useLazyAllTermQuery';
import { useLazyAllTermSession } from '../../graphql/useLazyAllTermSessionQuery';
import { useLazyAllUpcomingEvents } from '../../graphql/useLazyAllUpcomingEventsQuery';
import { useLazyAllUpcomingGroups } from '../../graphql/useLazyAllUpcomingGroupsQuery';
import { strings } from '../../localization';
import { useDirectoryDispatch, useDirectoryState } from '../../state/DirectoryState';
import { SavedFilterControl } from './SavedFilterControl';

export const FilterControls = () => {
    const { data, currentFilter } = useDirectoryState();
    const { security } = useAppContext();
    const { dispatch } = useDirectoryDispatch();
    const filterBarRef = createRef<IFilterBarRef>();

    useEffect(() => {
        if (currentFilter) {
            filterBarRef.current?.setInitialFilters(currentFilter);
        }
    }, [currentFilter, filterBarRef]);

    const getApplicationStatuses = useLazyAllApplicationStatuses();
    const getTerms = useLazyAllTerm();
    const getCategories = useLazyAllCategories();
    const getCommunities = useLazyAllCommunity();
    const getEntryStatus = useLazyAllEntryStatus();
    const getGenders = useLazyAllGenders();
    const getRoomTypes = useLazyAllRoomTypes();
    const getTermSessions = useLazyAllTermSession();
    const getUpcomingEvents = useLazyAllUpcomingEvents();
    const getUpcomingGroups = useLazyAllUpcomingGroups();

    const handleFilterChange = (currentFilter: ICurrentFilter[]) => {
        dispatch({ type: 'FILTER_GRID', currentFilter });
    };

    const handleFilterTypeAdd = (filter: ICurrentFilter): Promise<IData[]> => {
        return new Promise((resolve) => {
            switch (filter.name) {
                case 'Active Term Booking':
                    getTermSessions({}).then(({ data: filter }) => {
                        resolve(filter?.allTermSession.nodes.map((e) => ({ caption: e.caption, value: e.id })));
                    });
                    break;
                case 'Application Status':
                    getApplicationStatuses({}).then(({ data: filter }) => {
                        resolve(filter?.allApplicationStatuses.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Applied For Term':
                    getTerms({}).then(({ data: filter }) => {
                        resolve(filter?.allTerm.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Booking For Term':
                    getTerms({}).then(({ data: filter }) => {
                        resolve(filter?.allTerm.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Category':
                    getCategories({}).then(({ data: filter }) => {
                        resolve(filter?.allCategories.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Community':
                    getCommunities({}).then(({ data: filter }) => {
                        resolve(filter?.allCommunityFilterOptions.map((e) => ({ caption: e.description, value: e.enumValue })));
                    });
                    break;
                case 'Entry Status':
                    getEntryStatus({}).then(({ data: filter }) => {
                        resolve(filter?.allEntryStatus.map((e) => ({ caption: e.description, value: e.enumValue })));
                    });
                    break;
                case 'Gender':
                    getGenders({}).then(({ data: filter }) => {
                        resolve(filter?.allGenderEnum.nodes.map((e) => ({ caption: e.description, value: e.enumValue })));
                    });
                    break;
                case 'Room Types':
                    getRoomTypes({}).then(({ data: filter }) => {
                        resolve(filter?.allRoomTypes.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Upcoming Events':
                    getUpcomingEvents({}).then(({ data: filter }) => {
                        resolve(filter?.allUpcomingEvents.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Upcoming Groups':
                    getUpcomingGroups({}).then(({ data: filter }) => {
                        resolve(filter?.allUpcomingGroups.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
            }
        });
    };

    const handleSaveView = (id: string) => {
        dispatch({ type: 'SAVE_WORKSPACE', id });
    };

    return (
        <HStack inline gap="xxSmall">
            {data?.allMainDirectory?.currentView.listType === 'SAVED' || data?.allMainDirectory?.currentView.listType === 'SHARED_SAVED' ? (
                <>
                    <SavedFilterControl />

                    <SaveViewProvider onSave={handleSaveView} currentView={data?.allMainDirectory?.currentView}>
                        <SaveView />
                    </SaveViewProvider>
                </>
            ) : data?.mainDirectoryWorkspace?.isTooComplex ? (
                <TooComplexBar />
            ) : (
                security.entry.filter.canUpdateFilters && (
                    <>
                        {data?.allMainDirectory?.fixedFilters && <FixedFilters filters={data?.allMainDirectory?.fixedFilters} />}

                        <FilterBar
                            value={currentFilter}
                            ref={filterBarRef}
                            showSearch
                            onChange={handleFilterChange}
                            onFilterTypeAdd={handleFilterTypeAdd}
                        >
                            <BarFilter name={strings.MainDirectory_Label_ActiveTermBooking} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_ApplicationStatus} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_AppliedForTerm} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_BookingForTerm} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_Category} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_Community} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_EntryStatus} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_Gender} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_RoomTypes} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_UpcomingEvents} type="MultipleSelect" />
                            <BarFilter name={strings.MainDirectory_Label_UpcomingGroups} type="MultipleSelect" />
                        </FilterBar>

                        <SaveViewProvider onSave={handleSaveView} currentView={data?.allMainDirectory?.currentView}>
                            <SaveView />
                        </SaveViewProvider>
                    </>
                )
            )}
        </HStack>
    );
};
